import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { pmt2Service } from '../services/pmt2Service';
import { FarmingResultsProduct, FarmingResultsSelectionContextType } from '../types/farmingResultsTypes';

// LocalStorage keys
const LS_COMPANY_KEY = 'farmingResults_selectedCompany';
const LS_PRODUCT_ID_KEY = 'farmingResults_selectedProductId';

const FarmingResultsSelectionContext = createContext<FarmingResultsSelectionContextType | undefined>(undefined);

export const useFarmingResultsSelection = () => {
  const context = useContext(FarmingResultsSelectionContext);
  if (context === undefined) {
    throw new Error('useFarmingResultsSelection must be used within a FarmingResultsSelectionProvider');
  }
  return context;
};

interface FarmingResultsSelectionProviderProps {
  children: ReactNode;
}

export const FarmingResultsSelectionProvider: React.FC<FarmingResultsSelectionProviderProps> = ({ children }) => {
  const [companies, setCompanies] = useState<string[]>([]);
  // Initialize company from localStorage or default to empty string
  const [selectedCompany, _setSelectedCompany] = useState<string>(() => {
      return localStorage.getItem(LS_COMPANY_KEY) || '';
  });
  const [products, setProducts] = useState<FarmingResultsProduct[]>([]);
  // Initialize product later, after products are loaded
  const [selectedProduct, _setSelectedProduct] = useState<FarmingResultsProduct | null>(null);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(true);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false); // Initially false
  const [error, setError] = useState<string | null>(null);

  // Combined loading state
  const loading = loadingCompanies || loadingProducts;

  // Wrapper for setting company state and localStorage
  const setSelectedCompany = (company: string) => {
    try {
        localStorage.setItem(LS_COMPANY_KEY, company);
    } catch (e) {
        console.error("Failed to save company to localStorage", e);
    }
    _setSelectedCompany(company);
    // Reset product when company changes
    _setSelectedProduct(null);
    localStorage.removeItem(LS_PRODUCT_ID_KEY);
  };

  // Wrapper for setting product state and localStorage
  const setSelectedProduct = (product: FarmingResultsProduct | null) => {
    try {
        if (product) {
            localStorage.setItem(LS_PRODUCT_ID_KEY, product.id);
        } else {
            localStorage.removeItem(LS_PRODUCT_ID_KEY);
        }
    } catch (e) {
        console.error("Failed to save product ID to localStorage", e);
    }
    _setSelectedProduct(product);
  };

  // Load companies on component mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoadingCompanies(true);
        setError(null);
        const companiesData = await pmt2Service.getCompanies();
        setCompanies(companiesData);

        // Validate stored company or select first
        const storedCompany = localStorage.getItem(LS_COMPANY_KEY);
        if (storedCompany && companiesData.includes(storedCompany)) {
          _setSelectedCompany(storedCompany); // Keep stored company if valid
        } else if (companiesData.length > 0) {
          setSelectedCompany(companiesData[0]); // Select first and save to LS
        } else {
          setSelectedCompany(''); // No companies, clear selection
        }

      } catch (err) {
        console.error('Error loading companies:', err);
        setError('Failed to load companies. Please try again later.');
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, []); // Run only once on mount

  // Load products when company changes
  useEffect(() => {
    const loadProducts = async () => {
      if (!selectedCompany) {
          setProducts([]);
          _setSelectedProduct(null); // Use internal setter
          setLoadingProducts(false);
          return;
      };

      try {
        setLoadingProducts(true);
        setError(null);
        _setSelectedProduct(null); // Reset product selection while loading new ones
        const productsData = await pmt2Service.getProductsByCompany(selectedCompany);
        
        // Convert PMT2Product to FarmingResultsProduct (they have the same structure)
        const farmingProducts: FarmingResultsProduct[] = productsData.map(product => ({
          ...product
        }));
        
        setProducts(farmingProducts);

        // Validate stored product or select first
        const storedProductId = localStorage.getItem(LS_PRODUCT_ID_KEY);
        const foundProduct = storedProductId ? farmingProducts.find(p => p.id === storedProductId) : null;

        if (foundProduct) {
            _setSelectedProduct(foundProduct); // Restore stored product if valid
        } else if (farmingProducts.length > 0) {
            setSelectedProduct(farmingProducts[0]); // Select first and save ID to LS
        } else {
            setSelectedProduct(null); // No products, clear selection
        }

      } catch (err) {
        console.error('Error loading products:', err);
        setError(`Failed to load products for ${selectedCompany}. Please try again later.`);
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, [selectedCompany]); // Re-run when company changes

  return (
    <FarmingResultsSelectionContext.Provider
      value={{
        companies,
        selectedCompany,
        setSelectedCompany, // Use the wrapper function
        products,
        selectedProduct,
        setSelectedProduct, // Use the wrapper function
        loading,
        error
      }}
    >
      {children}
    </FarmingResultsSelectionContext.Provider>
  );
};